import { ReactNode, useEffect, useRef } from 'react';

import TamanLogo from '../../assets/TamanLogo.svg';

interface AuthLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  /** Extra Tailwind classes for the card (e.g. "md:h-[650px]") */
  cardClassName?: string;
  /** Header alignment; `start` puts the logo, title and subtitle on the left. */
  align?: 'center' | 'start';
  /** Shown next to the logo, e.g. an "Early access" label. */
  logoBadge?: ReactNode;
}

export const AuthLayout = ({
  children,
  title,
  subtitle,
  cardClassName = '',
  align = 'center',
  logoBadge,
}: AuthLayoutProps) => {
  const start = align === 'start';

  const containerRef = useRef<HTMLDivElement>(null);

  // iOS Safari: reset scroll position when the virtual keyboard dismisses.
  // When the keyboard closes the visual-viewport height jumps back up;
  // we detect that and scroll the page/container back to the top.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    let prevHeight = vv.height;

    const onResize = () => {
      const cur = vv.height;
      // Keyboard dismissed → viewport grew by a significant amount
      if (cur - prevHeight > 100) {
        window.scrollTo(0, 0);
        containerRef.current?.scrollTo(0, 0);
      }
      prevHeight = cur;
    };

    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  return (
    // The card centres with my-auto, not items-center: a card taller than the screen then still scrolls from its top.
    <div
      ref={containerRef}
      className="fixed inset-0 overflow-y-auto overscroll-contain bg-gradient-to-br from-green01/[0.15] to-black flex items-start justify-center px-2 py-3 md:p-4"
    >
      <div
        className={`
          relative z-10 w-full max-w-[480px] md:my-auto
          bg-greyP5/95 backdrop-blur-xl
          flex flex-col rounded-2xl
          border border-white/[0.06]
          shadow-[0_8px_60px_rgba(0,0,0,0.6)]
          px-2 py-4 md:px-8 md:py-6
          ${cardClassName}
        `}
      >
        <div
          className={`flex flex-col shrink-0 ${start ? 'items-start mb-4 px-1 md:px-2' : 'items-center mb-4 md:mb-5'}`}
        >
          <div className={`flex items-center ${start ? 'gap-3 mb-3' : 'gap-4 mb-4'}`}>
            <img src={TamanLogo} alt="Taman" className={`${start ? 'h-6 md:h-7' : 'h-8 md:h-10'} w-auto`} />
            {logoBadge}
          </div>

          {title && (
            <h1
              className={`text-white font-semibold ${
                start
                  ? 'text-2xl md:text-[28px] leading-tight text-left mb-0.5'
                  : 'text-2xl md:text-3xl text-center mb-1'
              }`}
            >
              {title}
            </h1>
          )}

          {subtitle && (
            <p className={`text-greyP2 ${start ? 'text-sm text-left' : 'text-xs text-center'}`}>{subtitle}</p>
          )}
        </div>

        <div className="flex-1 flex flex-col min-h-0 md:px-2">{children}</div>
      </div>
    </div>
  );
};
